import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { fmtErr } from "@/lib/shieldx";
import { Wordmark } from "@/components/shieldx/Logo";
import { Loader2, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import OrgTypeSelect, { INDIVIDUAL_TYPES } from "@/components/shieldx/OrgTypeSelect";
import { PasswordInput } from "@/components/shieldx/PasswordInput";

const SIZES = ["Individual", "1–10", "11–50", "51–200", "201–500", "501–1,000", "1,000+"];
const ROLES = ["Founder / Owner", "CEO", "CTO", "CIO", "CISO", "IT Administrator", "Security Administrator", "System Administrator", "Authorized Representative", "Individual Owner"];
const ASSET_TYPES = ["Website", "Web Application", "API", "Server", "Database", "Cloud", "Digital Service", "Other"];
const PROTECT_TARGETS = ["Websites", "Web Applications", "APIs", "Servers", "Databases", "Cloud Infrastructure", "Digital Services", "Internal Applications", "Government Digital Services", "Other Digital Systems"];
const STEPS = ["Account", "Organization", "Authorization", "Assets", "Verification"];

const field = "h-11 w-full rounded-lg border border-slate-800 bg-slate-900/60 px-3.5 text-sm text-slate-100 outline-none transition-colors focus:border-blue-500/60 focus:ring-2 focus:ring-blue-500/15";
const label = "mb-1.5 block text-xs font-medium tracking-wide text-slate-400";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const [acc, setAcc] = useState({ name: "", email: "", phone: "", job_title: "", country: "", password: "", confirm: "" });
  const [org, setOrg] = useState({ type: "", name: "", legal_name: "", website: "", email_domain: "", industry: "", size: "11–50", country: "", state: "", city: "", description: "" });
  const [contact, setContact] = useState({ name: "", email: "", role: "Founder / Owner", department: "", phone: "" });
  const [ack, setAck] = useState(false);
  const [targets, setTargets] = useState([]);
  const [asset, setAsset] = useState({ name: "", type: "Website", url: "", environment: "Production", criticality: "Medium" });

  const isIndividual = INDIVIDUAL_TYPES.includes(org.type);
  const s = (setter, obj) => (k) => (e) => setter({ ...obj, [k]: e.target.value });
  const sa = s(setAcc, acc), so = s(setOrg, org), sc = s(setContact, contact), sas = s(setAsset, asset);

  const validateStep = () => {
    if (step === 0) {
      if (!acc.name || !acc.email || !acc.password) return "Name, email and password are required.";
      if (acc.password.length < 8) return "Password must be at least 8 characters.";
      if (acc.password !== acc.confirm) return "Passwords do not match.";
      if (!org.type) return "Please select an organization type.";
    }
    if (step === 1 && !isIndividual && !org.name) return "Organization name is required.";
    if (step === 2 && !isIndividual && !ack) return "You must confirm you are authorized.";
    return "";
  };

  const next = () => {
    const v = validateStep();
    if (v) { setErr(v); return; }
    setErr("");
    if (isIndividual && step === 1) { setStep(3); return; } // skip authorization for individuals
    setStep(Math.min(step + 1, STEPS.length - 1));
  };
  const back = () => { setErr(""); if (isIndividual && step === 3) { setStep(1); return; } setStep(Math.max(step - 1, 0)); };

  const submit = async () => {
    setLoading(true); setErr("");
    try {
      await register({
        name: acc.name, email: acc.email, password: acc.password, phone: acc.phone,
        job_title: acc.job_title, country: acc.country, org_type: org.type,
        organization: isIndividual
          ? { type: "Individual", name: acc.name, display_name: acc.name, country: acc.country, size: "Individual", website: asset.url }
          : { type: org.type, name: org.name, legal_name: org.legal_name, display_name: org.name, website: org.website, email_domain: org.email_domain, industry: org.industry, size: org.size, country: org.country || acc.country, state: org.state, city: org.city, description: org.description },
        authorized_contact: isIndividual ? { name: acc.name, email: acc.email, role: "Individual Owner", phone: acc.phone } : contact,
        authorization_acknowledged: isIndividual ? true : ack,
        protect_targets: targets,
        assets: (asset.name || asset.url) ? [asset] : [],
      });
      toast.success("Welcome to ShieldX — registration received");
      navigate("/app/protection");
    } catch (ex) {
      setErr(fmtErr(ex?.response?.data?.detail) || ex.message);
      setStep(0);
    } finally { setLoading(false); }
  };

  const toggleTarget = (t) => setTargets(targets.includes(t) ? targets.filter((x) => x !== t) : [...targets, t]);
  const stepIndex = isIndividual && step > 1 ? step - 1 : step;

  return (
    <div className="min-h-screen bg-slate-950">
      <div className="mx-auto flex max-w-3xl flex-col px-6 py-10">
        <Link to="/"><Wordmark markSize={28} /></Link>

        <div className="mt-10">
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-50">Register your organization</h1>
          <p className="mt-1.5 text-sm text-slate-400">Which digital systems do you want to place behind a controlled protection and authorization boundary?</p>
        </div>

        {/* Stepper */}
        <div className="mt-8 flex items-center gap-1.5">
          {STEPS.map((st, i) => (
            <div key={st} className="flex flex-1 items-center gap-1.5">
              <div className={`flex items-center gap-2 ${i === stepIndex ? "text-blue-300" : i < stepIndex ? "text-emerald-300" : "text-slate-600"}`}>
                <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${i < stepIndex ? "bg-emerald-500/20" : i === stepIndex ? "bg-blue-600 text-white" : "bg-slate-800"}`}>
                  {i < stepIndex ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </span>
                <span className="hidden text-xs font-medium sm:block">{st}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`h-px flex-1 ${i < stepIndex ? "bg-emerald-500/40" : "bg-slate-800"}`} />}
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-xl border border-slate-800 bg-slate-900/40 p-6">
          {step === 0 && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className={label}>Full name</label><input data-testid="reg-name" className={field} value={acc.name} onChange={sa("name")} /></div>
              <div><label className={label}>Work email</label><input data-testid="reg-email" type="email" className={field} value={acc.email} onChange={sa("email")} /></div>
              <div><label className={label}>Mobile number</label><input data-testid="reg-phone" className={field} value={acc.phone} onChange={sa("phone")} /></div>
              <div><label className={label}>Job title / role</label><input data-testid="reg-job" className={field} value={acc.job_title} onChange={sa("job_title")} /></div>
              <div><label className={label}>Country</label><input data-testid="reg-country" className={field} value={acc.country} onChange={sa("country")} /></div>
              <div className="sm:col-span-2"><label className={label}>Organization type</label>
                <OrgTypeSelect value={org.type} onChange={(v) => setOrg({ ...org, type: v })} /></div>
              <div><label className={label}>Password</label><PasswordInput testid="reg-password" className={field} value={acc.password} onChange={sa("password")} autoComplete="new-password" /></div>
              <div><label className={label}>Confirm password</label><PasswordInput testid="reg-confirm" className={field} value={acc.confirm} onChange={sa("confirm")} autoComplete="new-password" /></div>
            </div>
          )}

          {step === 1 && !isIndividual && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className={label}>Organization name</label><input data-testid="reg-org-name" className={field} value={org.name} onChange={so("name")} /></div>
              <div><label className={label}>Legal name</label><input className={field} value={org.legal_name} onChange={so("legal_name")} /></div>
              <div><label className={label}>Website</label><input data-testid="reg-org-website" className={field} value={org.website} onChange={so("website")} placeholder="https://" /></div>
              <div><label className={label}>Official email domain</label><input className={field} value={org.email_domain} onChange={so("email_domain")} placeholder="company.com" /></div>
              <div><label className={label}>Industry / sector</label><input className={field} value={org.industry} onChange={so("industry")} /></div>
              <div><label className={label}>Organization size</label><select className={field} value={org.size} onChange={so("size")}>{SIZES.map((x) => <option key={x}>{x}</option>)}</select></div>
              <div><label className={label}>Country</label><input className={field} value={org.country} onChange={so("country")} /></div>
              <div><label className={label}>State / City</label><input className={field} value={org.city} onChange={so("city")} /></div>
              <div className="sm:col-span-2"><label className={label}>What does your organization do?</label><textarea className={`${field} h-20 py-2`} value={org.description} onChange={so("description")} /></div>
            </div>
          )}
          {step === 1 && isIndividual && (
            <div className="space-y-2 text-sm text-slate-400">
              <p>As an individual, we keep this simple. We'll use your account details and your website.</p>
              <div><label className={label}>Website URL</label><input data-testid="reg-ind-url" className={field} value={asset.url} onChange={sas("url")} placeholder="https://yourwebsite.com" /></div>
            </div>
          )}

          {step === 2 && !isIndividual && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className={label}>Authorized contact name</label><input className={field} value={contact.name} onChange={sc("name")} /></div>
              <div><label className={label}>Official email</label><input type="email" className={field} value={contact.email} onChange={sc("email")} /></div>
              <div><label className={label}>Authorization role</label><select className={field} value={contact.role} onChange={sc("role")}>{ROLES.map((r) => <option key={r}>{r}</option>)}</select></div>
              <div><label className={label}>Department</label><input className={field} value={contact.department} onChange={sc("department")} /></div>
              <div className="sm:col-span-2"><label className={label}>Contact number</label><input className={field} value={contact.phone} onChange={sc("phone")} /></div>
              <label className="flex items-start gap-2.5 sm:col-span-2 text-sm text-slate-300">
                <input type="checkbox" data-testid="reg-ack" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5 h-4 w-4 accent-blue-600" />
                I am authorized to register and manage this organization or asset.
              </label>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <div>
                <label className={label}>What would you like to protect?</label>
                <div className="flex flex-wrap gap-2">
                  {PROTECT_TARGETS.map((t) => (
                    <button key={t} type="button" onClick={() => toggleTarget(t)} className={`rounded-lg border px-3 py-1.5 text-xs ${targets.includes(t) ? "border-blue-500/50 bg-blue-600/15 text-blue-300" : "border-slate-700 bg-slate-800/60 text-slate-400"}`}>{t}</button>
                  ))}
                </div>
              </div>
              <div className="border-t border-slate-800 pt-4">
                <p className="mb-3 text-sm font-medium text-slate-200">Add your first asset (optional)</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div><label className={label}>Asset name</label><input data-testid="reg-asset-name" className={field} value={asset.name} onChange={sas("name")} /></div>
                  <div><label className={label}>Asset type</label><select className={field} value={asset.type} onChange={sas("type")}>{ASSET_TYPES.map((x) => <option key={x}>{x}</option>)}</select></div>
                  <div><label className={label}>URL / domain</label><input className={field} value={asset.url} onChange={sas("url")} placeholder="https://" /></div>
                  <div><label className={label}>Environment</label><select className={field} value={asset.environment} onChange={sas("environment")}>{["Production","Staging","Development","Testing"].map((x) => <option key={x}>{x}</option>)}</select></div>
                  <div><label className={label}>Criticality</label><select className={field} value={asset.criticality} onChange={sas("criticality")}>{["Low","Medium","High","Critical"].map((x) => <option key={x}>{x}</option>)}</select></div>
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <p className="text-sm text-slate-400">Review and submit. After registration you'll verify ownership and connect protection — we never mark an asset PROTECTED until its boundary is active.</p>
              <div className="space-y-2">
                {[["Account", acc.name, true], ["Organization", isIndividual ? acc.name : org.name, true], ["Ownership", "Pending", false], ["Asset", (asset.name || asset.url) ? "Added · Pending verification" : "None yet", false], ["Protection", "Pending", false]].map(([k, v, done]) => (
                  <div key={k} className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900/60 px-4 py-2.5">
                    <span className="font-mono text-[11px] uppercase tracking-wider text-slate-400">{k}</span>
                    <span className={`flex items-center gap-1.5 text-sm ${done ? "text-emerald-400" : "text-amber-300"}`}>{done ? <Check className="h-4 w-4" /> : null}{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {err && <p data-testid="reg-error" className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">{err}</p>}

          <div className="mt-6 flex items-center justify-between">
            <button onClick={back} disabled={step === 0} className="flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium text-slate-400 hover:text-slate-200 disabled:opacity-40"><ArrowLeft className="h-4 w-4" /> Back</button>
            {step < STEPS.length - 1 ? (
              <button onClick={next} data-testid="reg-next" className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-500">Continue <ArrowRight className="h-4 w-4" /></button>
            ) : (
              <button onClick={submit} disabled={loading} data-testid="reg-submit" className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Complete Registration <ArrowRight className="h-4 w-4" /></>}
              </button>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-sm text-slate-500">Already registered? <Link to="/login" className="font-medium text-blue-400 hover:text-blue-300">Sign in</Link></p>
        <p className="mt-8 text-center font-mono text-[11px] tracking-wide text-slate-600">Powered by Vijay Software Solutions Pvt Ltd</p>
      </div>
    </div>
  );
}
